import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  createDataTableColumnHelper,
  DataTable,
} from "@beweegbaas/ui";
import type { components } from "#/schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import { z } from "zod";
import { useDateFormatter } from "#/hooks/use-date-formatter";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { ShortDate } from "#/components/short-date";
import { AveragesTable } from "#/modules/school.klassen/components/averages-table";
import { getStudentDashboard } from "#/modules/school.students/students.functions";
import {
  OverachievedIconButton,
  ScoreStatus,
  ScoreStatusIndicatorLabel,
} from "#/modules/util.score-status/components/score-status";
import { getScoreStatus } from "#/modules/util.score-status/score-status";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { Leerjaar } from "#/schema.nswag";

const dashboardSearchSchema = z.object({
  // AveragesTable owns these URL params (TanStack search): `leerjaar` drives the averages loader,
  // `tab` is display-only.
  // Links from before the leerjaren list held single leerjaren only can carry a combined one
  // ("Vijf, Zes"): fall back to the default leerjaar instead of failing the whole route.
  leerjaar: apiEnum(Leerjaar).optional().catch(undefined),
  tab: z.string().optional(),
});

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/dashboard",
)({
  validateSearch: dashboardSearchSchema,
  loaderDeps: ({ search }) => ({ leerjaar: search.leerjaar }),
  loader: ({ params, deps }) =>
    getStudentDashboard({ data: { leerlingId: params.id, leerjaar: deps.leerjaar } }),
  component: Dashboard,
  errorComponent: ({ error }) => <ErrorBoundaryComponent error={error} />,
});

function formatAverage(average: number): string {
  return average === 0 ? "-" : average.toFixed(1);
}

function formatResultaat(
  behaaldNiveau: number | null | undefined,
  maxNiveau: number,
  type: components["schemas"]["BeweegactiviteitType"],
) {
  if (behaaldNiveau == null || behaaldNiveau <= 0) return "-";
  // Meerkeuze counts ticked doelen, so show count/max like the SE resultaten do.
  return type === "Meerkeuze" ? `${behaaldNiveau}/${maxNiveau}` : behaaldNiveau.toString();
}

type DashboardData = NonNullable<Awaited<ReturnType<typeof getStudentDashboard>>["dashboardData"]>;
type NotitieRow = NonNullable<DashboardData["latestNotities"]>[number];
type BeweegRow = NonNullable<DashboardData["latestBeweegactiviteiten"]>[number];
type SociaalRow = NonNullable<DashboardData["latestSociaalEmotioneelThemas"]>[number];
type RapportRow = NonNullable<DashboardData["latestRapporten"]>[number];

// Matches the legacy `size="card"` cell padding so table content aligns with the card header.
const cardTableClassName = "[&_th]:px-6 [&_td]:px-6";

// The cards sit at half width: the name column wraps, and the result and date columns keep a
// fixed width so the two result cards line up.
const resultaatColumnMeta = { headerClassName: "w-28", cellClassName: "w-28" };
const datumColumnMeta = { headerClassName: "w-32", cellClassName: "w-32" };

const beweegHelper = createDataTableColumnHelper<BeweegRow>();
const beweegColumns = beweegHelper.columns([
  beweegHelper.accessor("name", {
    header: () => m["student.dashboard.latest_movement_results.movement_activity"](),
    meta: { wrap: true },
  }),
  beweegHelper.display({
    id: "resultaat",
    header: () => m["common.resultaat"](),
    meta: resultaatColumnMeta,
    cell: ({ row }) =>
      row.original.behaaldNiveau === 0 ? (
        "-"
      ) : (
        <ScoreStatusIndicatorLabel
          status={getScoreStatus(row.original.behaaldNiveau, row.original.maxNiveau)}
          label={formatResultaat(
            row.original.behaaldNiveau,
            row.original.maxNiveau,
            row.original.type,
          )}
        />
      ),
  }),
  beweegHelper.display({
    id: "datum",
    header: () => m["common.fields.date"](),
    meta: datumColumnMeta,
    cell: ({ row }) =>
      row.original.behaaldAt ? <ShortDate value={row.original.behaaldAt} /> : "-",
  }),
]);

const sociaalHelper = createDataTableColumnHelper<SociaalRow>();
const sociaalColumns = sociaalHelper.columns([
  sociaalHelper.accessor("name", {
    header: () => m["student.dashboard.latest_social_emotional_results.theme"](),
    meta: { wrap: true },
  }),
  sociaalHelper.display({
    id: "resultaat",
    header: () => m["common.resultaat"](),
    meta: resultaatColumnMeta,
    cell: ({ row }) =>
      row.original.behaaldNiveau === 0 ? (
        "-"
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <ScoreStatusIndicatorLabel
            status={getScoreStatus(row.original.behaaldNiveau, row.original.maxNiveau)}
            label={row.original.resultaat}
          />
          {row.original.isOverachieved && <OverachievedIconButton />}
        </span>
      ),
  }),
  sociaalHelper.display({
    id: "datum",
    header: () => m["common.fields.date"](),
    meta: datumColumnMeta,
    cell: ({ row }) =>
      row.original.behaaldAt ? <ShortDate value={row.original.behaaldAt} /> : "-",
  }),
]);

const notitieHelper = createDataTableColumnHelper<NotitieRow>();
const notitieColumns = notitieHelper.columns([
  notitieHelper.display({
    id: "notitie",
    header: () => m["common.notitie"](),
    meta: { wrap: true, cellClassName: "py-2" },
    cell: ({ row }) => <p className="line-clamp-2 text-sm">{row.original.notitie || "-"}</p>,
  }),
  notitieHelper.display({
    id: "datum",
    header: () => m["common.fields.date"](),
    meta: { ...datumColumnMeta, cellClassName: "w-32 py-2" },
    cell: ({ row }) =>
      row.original.afgerondAt ? <ShortDate value={row.original.afgerondAt} /> : "-",
  }),
]);

const rapportHelper = createDataTableColumnHelper<RapportRow>();

function Dashboard() {
  const loaderData = Route.useLoaderData();
  const dateFormatter = useDateFormatter({
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const { dashboardData } = loaderData;
  const statistieken = dashboardData?.statistieken;
  const latestNotities = dashboardData?.latestNotities || [];
  const latestBeweegactiviteiten = dashboardData?.latestBeweegactiviteiten || [];
  const latestSociaalEmotioneelThemas = dashboardData?.latestSociaalEmotioneelThemas || [];
  const latestRapporten = dashboardData?.latestRapporten || [];

  const rapportColumns = useMemo(
    () =>
      rapportHelper.columns([
        rapportHelper.display({
          id: "name",
          header: () => m["student.dashboard.latest_reports.name"](),
          meta: { wrap: true, rowTarget: true },
          cell: ({ row }) => {
            const rapport = row.original;
            const hasKlassen = rapport.klassen && rapport.klassen.length > 0;
            const firstKlasId = rapport.klassen?.[0]?.id;
            return hasKlassen && firstKlasId ? (
              <Link
                from={Route.fullPath}
                to="/$locale/{-$schoolyear}/$school/reports/flow/$id/step/3/$class"
                params={(prev: Record<string, string>) => ({
                  ...prev,
                  id: rapport.id,
                  class: firstKlasId,
                })}
                className="text-secondary-foreground underline underline-offset-2"
              >
                {rapport.name || m["student.dashboard.latest_reports.name"]()}
              </Link>
            ) : (
              <Link
                from={Route.fullPath}
                to="/$locale/{-$schoolyear}/$school/reports/flow/$id/step/2"
                params={(prev: Record<string, string>) => ({ ...prev, id: rapport.id })}
                className="text-secondary-foreground underline underline-offset-2"
              >
                {rapport.name || m["student.dashboard.latest_reports.name"]()}
              </Link>
            );
          },
        }),
        rapportHelper.display({
          id: "period",
          header: () => m["common.labels.periode"](),
          meta: { headerClassName: "w-58", cellClassName: "w-58" },
          cell: ({ row }) => {
            const rapport = row.original;
            const periodLabel =
              rapport.from && rapport.to
                ? `${dateFormatter.format(new Date(rapport.from))} – ${dateFormatter.format(new Date(rapport.to))}`
                : "-";
            return (
              <Badge variant="gray" size="sm">
                {periodLabel}
              </Badge>
            );
          },
        }),
      ]),
    [dateFormatter],
  );

  return (
    <div className="flex flex-col gap-6">
      {statistieken && (
        <Card className="grid gap-0 py-0 max-lg:divide-y lg:grid-cols-5 lg:divide-x">
          <Kpi label={m["student.dashboard.statistics.school_career_average"]()}>
            <ScoreStatus
              status={getScoreStatus(statistieken.schoolCareerAverage, 5)}
              label={formatAverage(statistieken.schoolCareerAverage)}
              className="h-6 w-auto text-base"
            />
          </Kpi>

          <Kpi label={m["student.dashboard.statistics.school_year_average"]()}>
            <ScoreStatus
              status={getScoreStatus(statistieken.schooljaarAverage, 5)}
              label={formatAverage(statistieken.schooljaarAverage)}
              className="h-6 w-auto text-base"
            />
          </Kpi>

          <Kpi label={m["student.dashboard.statistics.attendance"]()}>
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <Badge variant="gray" size="lg">
                {statistieken.totalAanwezig}
              </Badge>
              <div>
                {statistieken.totalTeLaat > 0 &&
                  ` ${statistieken.totalTeLaat}x ${m["student.dashboard.statistics.late"]()}`}
                {statistieken.totalGeblesseerd > 0 &&
                  ` ${statistieken.totalGeblesseerd}x ${m["student.dashboard.statistics.injured"]()}`}
                {statistieken.totalZiek > 0 &&
                  ` ${statistieken.totalZiek}x ${m["student.dashboard.statistics.sick"]()}`}
                {statistieken.totalAnders > 0 &&
                  ` ${statistieken.totalAnders}x ${m["student.dashboard.statistics.other"]()}`}
              </div>
            </div>
          </Kpi>

          <Kpi label={m["student.dashboard.statistics.total_absent"]()}>
            <Badge variant="destructive" size="lg">
              {statistieken.totalAfwezig}
            </Badge>
          </Kpi>

          <Kpi label={m["student.dashboard.statistics.gym_clothes_forgotten"]()}>
            <Badge
              variant={statistieken.totalGymkledingVergeten > 0 ? "destructive" : "gray"}
              size="lg"
            >
              {statistieken.totalGymkledingVergeten}
            </Badge>
          </Kpi>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card size="sm" className="bg-muted flex h-full pb-0! shadow-none ring-0 xl:col-span-2">
          <CardContent className="flex grow px-0!">
            <AveragesTable
              mode="single-leerling"
              className="w-full grow"
              tabsClassName="px-6"
              leerlijnData={loaderData.leerlijnAveragesData}
              sociaalEmotioneelData={loaderData.sociaalEmotioneelThemaDomeinAveragesData}
              withContainer={false}
            />
          </CardContent>
        </Card>

        <Card className="bg-muted pb-0 shadow-none ring-0">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <CardTitle>{m["student.dashboard.latest_movement_results.title"]()}</CardTitle>
            <Button
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/students/$id/results"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
              size="sm"
              variant="secondary"
            >
              <IconMaterialSymbolsArrowRightAltRounded />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {latestBeweegactiviteiten.length === 0 ? (
              <p className="text-muted-foreground px-6 pb-6 text-sm">
                {m["student.dashboard.latest_movement_results.no_results"]()}
              </p>
            ) : (
              <DataTable
                className={cardTableClassName}
                aria-label={m["student.dashboard.latest_movement_results.title"]()}
                columns={beweegColumns}
                data={latestBeweegactiviteiten}
                getRowId={(item) => item.beweegactiviteitId}
              />
            )}
          </CardContent>
        </Card>

        <Card className="bg-muted pb-0 shadow-none ring-0">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <CardTitle>{m["student.dashboard.latest_social_emotional_results.title"]()}</CardTitle>
            <Button
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/students/$id/social-results"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
              size="sm"
              variant="secondary"
            >
              <IconMaterialSymbolsArrowRightAltRounded />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {latestSociaalEmotioneelThemas.length === 0 ? (
              <p className="text-muted-foreground px-6 pb-6 text-sm">
                {m["student.dashboard.latest_social_emotional_results.no_results"]()}
              </p>
            ) : (
              <DataTable
                className={cardTableClassName}
                aria-label={m["student.dashboard.latest_social_emotional_results.title"]()}
                columns={sociaalColumns}
                data={latestSociaalEmotioneelThemas}
                getRowId={(item) => item.sociaalEmotioneelThemaId}
              />
            )}
          </CardContent>
        </Card>

        <Card className="bg-muted pb-0 shadow-none ring-0">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <CardTitle>{m["student.dashboard.latest_notes.title"]()}</CardTitle>
            <Button
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/students/$id/notities"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
              size="sm"
              variant="secondary"
            >
              <IconMaterialSymbolsArrowRightAltRounded />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {latestNotities.length === 0 ? (
              <p className="text-muted-foreground px-6 pb-6 text-sm">
                {m["student.dashboard.latest_notes.no_notes"]()}
              </p>
            ) : (
              <DataTable
                className={cardTableClassName}
                aria-label={m["student.dashboard.latest_notes.title"]()}
                columns={notitieColumns}
                data={latestNotities}
                getRowId={(notitie) => notitie.id}
              />
            )}
          </CardContent>
        </Card>

        <Card className="bg-muted pb-0 shadow-none ring-0">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <CardTitle>{m["student.dashboard.latest_reports.title"]()}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {latestRapporten.length === 0 ? (
              <p className="text-muted-foreground px-6 pb-6 text-sm">
                {m["student.dashboard.latest_reports.no_reports"]()}
              </p>
            ) : (
              <DataTable
                className={cardTableClassName}
                aria-label={m["student.dashboard.latest_reports.title"]()}
                columns={rapportColumns}
                data={latestRapporten}
                getRowId={(rapport) => rapport.id}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 px-4 py-3.5">
      <div className="text-sm font-medium">{label}</div>
      <div className="mt-auto flex items-center">{children}</div>
    </div>
  );
}
